// Core domain model for the change management V2 prototype.
//
// This is an in-memory mock model. A Change is ONE persistent record that moves
// through the lifecycle. It is deliberately not a digital copy of the paper
// CASE-RQ / CASE-PLAN / CAPA-PLAN forms; request, assessment and
// approval are separate digital steps that all read and write this single record.

// -- Stable enums (as-const objects + derived unions, mirroring the V1 mock idiom)

export const CHANGE_STATUS = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  IN_ASSESSMENT: 'In assessment',
  INITIAL_PLANNING: 'Initial planning',
  IN_EXECUTION: 'In execution',
  IN_COMPLETION: 'In completion',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
} as const;
export type TChangeStatus = (typeof CHANGE_STATUS)[keyof typeof CHANGE_STATUS];

export const LIFECYCLE_STEP = {
  REQUEST: 'Request',
  ASSESSMENT: 'Assessment',
  PLANNING: 'Planning',
  EXECUTION: 'Execution',
  COMPLETION: 'Completion',
} as const;
export type TLifecycleStep = (typeof LIFECYCLE_STEP)[keyof typeof LIFECYCLE_STEP];

export const CHANGE_CATEGORY = {
  MAINTENANCE: 'Maintenance, optimization or extension',
  NONCONFORMITY: 'Nonconformity or potential nonconformity',
} as const;
export type TChangeCategory = (typeof CHANGE_CATEGORY)[keyof typeof CHANGE_CATEGORY];

export const CHANGE_TYPE = {
  PRODUCT: 'Product',
  PRODUCTION: 'Production',
  QMS: 'QMS',
  MIXED: 'Mixed',
} as const;
export type TChangeType = (typeof CHANGE_TYPE)[keyof typeof CHANGE_TYPE];

export const SCOPE_TYPE = {
  PRODUCT: 'Product',
  QMS: 'QMS',
  QMS_AND_TD: 'QMS + TD',
} as const;
export type TScopeType = (typeof SCOPE_TYPE)[keyof typeof SCOPE_TYPE];

export const CRITICALITY = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
} as const;
export type TCriticality = (typeof CRITICALITY)[keyof typeof CRITICALITY];

export const URGENCY = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
} as const;
export type TUrgency = (typeof URGENCY)[keyof typeof URGENCY];

export const SAFETY_CONCERN = {
  YES: 'Yes',
  NO: 'No',
  UNKNOWN: 'Unknown',
} as const;
export type TSafetyConcern = (typeof SAFETY_CONCERN)[keyof typeof SAFETY_CONCERN];

export const SOURCE_TYPE = {
  COMPLAINT: 'Complaint',
  AUDIT_FINDING: 'Audit finding',
  CAPA: 'CAPA',
  PMS_SIGNAL: 'PMS signal',
  SUPPLIER_ISSUE: 'Supplier issue',
  NONCONFORMING_PRODUCT: 'Nonconforming product',
  STANDARD_OR_REGULATION: 'Standard or regulation',
} as const;
export type TSourceType = (typeof SOURCE_TYPE)[keyof typeof SOURCE_TYPE];

export const ASSESSOR_FUNCTION = {
  SUPPLIER_QUALITY: 'Supplier quality',
  ENGINEERING: 'Engineering or R&D',
  QUALITY_ASSURANCE: 'Quality assurance',
  REGULATORY: 'Regulatory affairs',
  BIOLOGICAL_SAFETY: 'Biological safety',
  PRODUCTION: 'Production or operations',
  PRRC: 'PRRC or vigilance',
  SOFTWARE: 'Software',
  CLINICAL: 'Clinical',
  TRAINING: 'Training',
} as const;
export type TAssessorFunction = (typeof ASSESSOR_FUNCTION)[keyof typeof ASSESSOR_FUNCTION];

export const TRIAGE_DECISION = {
  ACCEPT: 'Accept for assessment',
  RETURN: 'Return for clarification',
  REJECT: 'Reject',
  DUPLICATE: 'Mark as duplicate',
} as const;
export type TTriageDecision = (typeof TRIAGE_DECISION)[keyof typeof TRIAGE_DECISION];

export const CREATION_METHOD = {
  BLANK: 'Blank change',
  TEMPLATE: 'Use change template',
  PREVIOUS: 'Reuse previous change',
  SOURCE: 'Create from source',
} as const;
export type TCreationMethod = (typeof CREATION_METHOD)[keyof typeof CREATION_METHOD];

// -- Reference entities (people, products, documents, source records)

export type TMockUser = {
  id: string;
  name: string;
  email: string;
  jobTitle: string;
};

export type TMockProduct = {
  id: string;
  name: string;
  family: string;
};

export type TMockDocument = {
  id: string;
  name: string;
  documentNumber: string;
};

export type TSourceRecord = {
  id: string;
  reference: string;
  type: TSourceType;
  title: string;
  summary: string;
  /** Set when the source record is a confirmed nonconformity (blocks Reject under the mock tenant policy). */
  confirmedNonconformity?: boolean;
};

// -- Supporting sub-records held on a Change

export type TAttachment = {
  id: string;
  name: string;
  sizeLabel: string;
  uploadedBy: string;
  uploadedAt: string;
};

export type TComment = {
  id: string;
  author: string;
  authorRole: string;
  createdAt: string;
  body: string;
};

export type TChangeTask = {
  id: string;
  title: string;
  kind: 'assessment' | 'containment';
  assignedFunction?: TAssessorFunction | string;
  owner?: string;
  dueDate?: string;
  status: 'Open' | 'In progress' | 'Done';
  urgent?: boolean;
};

export type TContainment = {
  required: boolean;
  description?: string;
  owner?: string;
  dueDate?: string;
};

export type TChangeCompletion = {
  effectivenessReviewRequired: boolean;
  releaseReadinessCheckedAt?: string;
  releaseReadinessCheckedBy?: string;
  releaseApprovedAt?: string;
  releaseApprovedBy?: string;
  effectivenessReviewedAt?: string;
  effectivenessReviewedBy?: string;
  closedAt?: string;
  closedBy?: string;
};

export type TReturnInfo = {
  rationale: string;
  returnedBy: string;
  returnedAt: string;
};

export type TDuplicateInfo = {
  duplicateOfId: string;
  duplicateOfDisplayId: string;
  rationale: string;
};

export type TRejectInfo = {
  rationale: string;
  rejectedBy: string;
  rejectedAt: string;
};

// -- The single connected Change record

export type TChangeV2 = {
  // Identity
  id: string; // technical internal id, always present
  requestId: string; // human-readable REQ-YYYY-####, available from draft creation
  displayId?: string; // human-readable CHG-YYYY-#### assigned on request submission
  title: string;
  status: TChangeStatus;
  lifecycleStep: TLifecycleStep;

  // People and dates
  initiator: string;
  changeManager?: string;
  createdAt: string;
  requestDate: string;
  targetDate?: string;

  // Classification
  changeCategory?: TChangeCategory;
  changeType?: TChangeType;
  scopeType?: TScopeType;
  criticality?: TCriticality;
  urgency: TUrgency;

  // Request narrative
  description: string;
  reasonOrTrigger: string;
  desiredOutcome?: string;
  implementationProposal?: string;

  // Initial scope
  affectedProducts: string[];
  affectedProductFamilies: string[];
  affectedQmsAreas: string[];
  sourceRecord?: string; // source record id
  knownSafetyConcern?: TSafetyConcern;
  safetyConcernDescription?: string;
  knownAffectedDocuments: string[];

  // Relationships and supporting info
  relatedChanges: string[];
  collaborators: string[];
  attachments: TAttachment[];

  // CAPA linkage (linked, never auto-decided)
  capaRequired?: boolean;
  capaId?: string;

  // Progress / attention
  progress: number; // 0..100
  blockers: string[];
  nextAction?: string;
  currentOwner?: string;
  awaitingExternalDecision?: boolean;

  // Assessment routing
  template?: string; // applied template id
  templateVersion?: number; // applied template version (snapshotted on acceptance)
  // Immutable record of the template inputs a draft was generated from. Stored at
  // draft creation so later template edits never alter an existing Change.
  templateSnapshot?: TInstantiatedTemplateSnapshot;
  changeSubtype?: string; // the more specific template subtype, stored separately from scope
  assessorFunctions: TAssessorFunction[];
  immediateContainment: TContainment;
  workflowRoute: TLifecycleStep[];

  // Assessment record (Screens 5 and 6). Instantiated from the template snapshot
  // when the request is submitted; absent until then.
  assessment?: TChangeAssessment;

  // Implementation plan and execution record (Screens 7 and 8). Built from the
  // approved assessment when planning begins; frozen into a baseline on execution.
  plan?: TChangePlan;

  // Release readiness, release approval, optional effectiveness and closure.
  completion?: TChangeCompletion;

  // Prototype-only bookkeeping
  returnInfo?: TReturnInfo;
  duplicateInfo?: TDuplicateInfo;
  rejectInfo?: TRejectInfo;

  auditEvents: TAuditEvent[];
  comments: TComment[];
  tasks: TChangeTask[];
};

// Imported here to keep the Change record self-describing without a circular file.
import type { TAuditEvent } from './audit';
import type { TChangeAssessment } from './assessment';
import type { TChangePlan } from './plan';
import type { TInstantiatedTemplateSnapshot } from './templateInputs';
